import { useTranslation } from 'react-i18next';
import {
  AlgComponentType,
  ShiftAssignment,
  User,
  ShiftAssignmentType,
  Section,
} from '@youshift/shared/types';
import { useState, useMemo } from 'react';
import {
  formatUserName,
  dateToString,
  returnColor,
} from '@youshift/shared/utils';
import { Tab } from '@headlessui/react';
import { ComponentPrioritySchema } from '@youshift/shared/hooks/mutations';

import { iconTypes } from '../../utils/constants';
import ShiftOutputLegend from '../Calendars/ShiftOutputLegend';
import PreassingedShiftPriorityByUser from './PreassingedShiftPriorityByUser';
import PreassignedShiftPriorityByType from './PreassignedShiftPriorityByType';
import PreassignedShiftPriorityBySection from './PreassignedShiftPriorityBySection';
import SectionLegend from '../Calendars/SectionLegend';
import { useItrContext } from '../../layouts/IterationRootLayout/IterationRootLayout';
import InfoIcon from '../InfoIcon';
import { getConstraintCountByType } from './utils';

interface PreassignedShiftPriorityProps {
  users: Record<number, User>;
  shiftAssignments: Record<number, ShiftAssignment>;
  importanceValues: ComponentPrioritySchema;
  setImportanceValues: (
    fn: (prev: ComponentPrioritySchema) => ComponentPrioritySchema,
  ) => void;
}

export default function PreassignedShiftPriority({
  users,
  shiftAssignments,
  importanceValues,
  setImportanceValues,
}: PreassignedShiftPriorityProps) {
  const { t } = useTranslation();

  const { sectionsWithSlots } = useItrContext();

  const preassignedShiftConstraints = useMemo(
    () =>
      getConstraintCountByType(
        importanceValues,
        AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS,
      ),
    [importanceValues],
  );

  // Create a dictionary of sections for quick lookup
  const sectionsDict = sectionsWithSlots
    .filter(section => section.section.assigned_by_smartass)
    .reduce(
      (acc, { section }) => {
        acc[section.id_section] = section;
        return acc;
      },
      {} as Record<number, Section>,
    );
  // Get unique users with preassigned shifts
  const usersWithPreassignedShifts = Object.values(shiftAssignments).reduce(
    (acc, assignment) => {
      if (!acc[assignment.id_user]) {
        acc[assignment.id_user] = [];
      }
      acc[assignment.id_user].push(assignment);
      return acc;
    },
    {} as Record<number, ShiftAssignment[]>,
  );

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-medium">
          {t(
            'manager.smartass.algorithmSettingsModal.preassignedShiftPriority.title',
          )}
        </h3>
        <InfoIcon
          title={t(
            'manager.smartass.algorithmSettingsModal.preassignedShiftPriority.title',
          )}
          content={
            <>
              <p className="pt-4">
                {t(
                  'manager.smartass.algorithmSettingsModal.preassignedShiftPriority.description',
                )}
              </p>
              <p className="mt-2 font-medium">
                {t('manager.smartass.algorithmSettingsModal.constraintCount', {
                  count: preassignedShiftConstraints,
                })}
              </p>
            </>
          }
          className="w-4 h-4"
          iconSize={6}
          size="2xl"
        />
      </div>
      <div className="flex flex-col gap-1 p-4">
        <ShiftOutputLegend />
        <SectionLegend sections={Object.values(sectionsDict)} />
      </div>
      <Tab.Group>
        <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-4">
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5
              ${
                selected
                  ? 'bg-white text-blue-700 shadow'
                  : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
              }`
            }
          >
            {t(
              'manager.smartass.algorithmSettingsModal.preassignedShiftPriority.byType',
            )}
          </Tab>
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5
              ${
                selected
                  ? 'bg-white text-blue-700 shadow'
                  : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
              }`
            }
          >
            {t(
              'manager.smartass.algorithmSettingsModal.preassignedShiftPriority.byUser',
            )}
          </Tab>
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5
              ${
                selected
                  ? 'bg-white text-blue-700 shadow'
                  : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
              }`
            }
          >
            {t(
              'manager.smartass.algorithmSettingsModal.preassignedShiftPriority.bySection',
            )}
          </Tab>
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel>
            <PreassignedShiftPriorityByType
              shiftAssignments={shiftAssignments}
              users={users}
              importanceValues={importanceValues}
              setImportanceValues={setImportanceValues}
            />
          </Tab.Panel>
          <Tab.Panel>
            <PreassingedShiftPriorityByUser
              users={users}
              importanceValues={importanceValues}
              setImportanceValues={setImportanceValues}
              usersWithPreassignedShifts={usersWithPreassignedShifts}
            />
          </Tab.Panel>
          <Tab.Panel>
            <PreassignedShiftPriorityBySection
              shiftAssignments={shiftAssignments}
              users={users}
              importanceValues={importanceValues}
              setImportanceValues={setImportanceValues}
            />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
